import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Heart, BookOpen, Users, ArrowRight, ChevronRight, CheckCircle, Sparkles, PartyPopper, Gift } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { salvationSchema } from '../../utils/validators'
import { addDocument } from '../../services/firebase'
import { Input, Select, Checkbox } from '../../components/common'

const salvationSteps = [
  {
    title: 'Recognize Your Need',
    scripture: 'For all have sinned and fall short of the glory of God.',
    reference: 'Romans 3:23',
    description: 'The first step is acknowledging that we all have sinned and need a Savior. No one is perfect, and we cannot save ourselves.',
    gradient: 'from-rose-500 to-pink-500',
  },
  {
    title: "Understand God's Love",
    scripture: 'For God so loved the world that He gave His only begotten Son, that whoever believes in Him should not perish but have everlasting life.',
    reference: 'John 3:16',
    description: "God loves you so much that He sent Jesus to die for your sins. This is the greatest demonstration of love the world has ever seen.",
    gradient: 'from-primary-500 to-blue-500',
  },
  {
    title: 'Accept the Gift',
    scripture: 'For the wages of sin is death, but the gift of God is eternal life in Christ Jesus our Lord.',
    reference: 'Romans 6:23',
    description: 'Salvation is a free gift from God. You cannot earn it by good works—you simply receive it by faith in Jesus Christ.',
    gradient: 'from-secondary-500 to-purple-500',
  },
  {
    title: 'Confess and Believe',
    scripture: 'If you confess with your mouth the Lord Jesus and believe in your heart that God has raised Him from the dead, you will be saved.',
    reference: 'Romans 10:9',
    description: 'Declare Jesus as your Lord and believe in your heart. This is the moment of transformation when you become a child of God.',
    gradient: 'from-accent-500 to-orange-500',
  },
  {
    title: 'New Life in Christ',
    scripture: 'Therefore, if anyone is in Christ, he is a new creation; old things have passed away; behold, all things have become new.',
    reference: '2 Corinthians 5:17',
    description: 'When you accept Christ, you become a new person! Your past is forgiven, and you have a fresh start with God.',
    gradient: 'from-emerald-500 to-teal-500',
  },
]

const nextSteps = [
  {
    icon: BookOpen,
    title: 'Start Reading the Bible',
    description: "Begin with the Gospel of John to learn about Jesus. Set aside time daily for God's Word.",
    gradient: 'from-primary-500 to-blue-500',
  },
  {
    icon: Users,
    title: 'Join a Small Group',
    description: 'Connect with other believers who can encourage you and help you grow in your faith.',
    gradient: 'from-secondary-500 to-purple-500',
  },
  {
    icon: Heart,
    title: 'Get Baptized',
    description: "Baptism is your public declaration of faith in Christ. It's an important step of obedience.",
    gradient: 'from-accent-500 to-orange-500',
  },
]

const SalvationJourney = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [showDecision, setShowDecision] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(salvationSchema),
  })

  const onSubmit = async (data) => {
    try {
      await addDocument('formSubmissions/decisions/records', data)
      setSubmitted(true)
      reset()
    } catch (error) {
      console.error('Error submitting decision:', error)
    }
  }

  const progress = ((currentStep + 1) / salvationSteps.length) * 100

  if (submitted) {
    return (
      <motion.div
        className="text-center py-12"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          className="mb-8"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
        >
          <div className="w-28 h-28 bg-gradient-to-br from-accent-400 via-secondary-500 to-primary-500 rounded-full flex items-center justify-center mx-auto shadow-2xl">
            <PartyPopper className="w-14 h-14 text-white" />
          </div>
        </motion.div>

        <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100 mb-4">
          Welcome to God's <span className="gradient-text">Family!</span>
        </h2>
        <p className="text-xl text-dark-300 mb-10 max-w-2xl mx-auto">
          This is the most important decision you'll ever make. Heaven is rejoicing over you!
          We're so excited to walk with you on this journey.
        </p>

        <div className="bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 rounded-3xl p-8 max-w-2xl mx-auto mb-10 border border-primary-100">
          <h3 className="font-heading font-bold text-2xl text-dark-100 mb-8 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-accent-500" />
            Your Next Steps
          </h3>
          <div className="grid gap-6">
            {nextSteps.map((step, index) => (
              <motion.div
                key={step.title}
                className="flex items-start gap-4 text-left bg-white rounded-2xl p-5 shadow-soft"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${step.gradient} rounded-2xl shadow-lg flex items-center justify-center flex-shrink-0`}>
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-dark-100 mb-1">{step.title}</h4>
                  <p className="text-dark-300 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/plan-your-visit">
            <motion.button
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold shadow-lg shadow-primary-500/25"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Plan Your First Visit
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
          <Link to="/contact">
            <motion.button
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-secondary-500 text-secondary-600 font-bold hover:bg-secondary-50 transition-colors"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Connect With Us
            </motion.button>
          </Link>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-10">
        <div className="flex justify-between text-sm text-dark-300 mb-3">
          <span className="font-semibold">Your Journey</span>
          <span className="font-semibold">Step {currentStep + 1} of {salvationSteps.length}</span>
        </div>
        <div className="h-3 bg-light-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
        {/* Step Dots */}
        <div className="flex justify-between mt-4">
          {salvationSteps.map((step, index) => (
            <motion.button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                index <= currentStep
                  ? `bg-gradient-to-r ${step.gradient} text-white shadow-lg`
                  : 'bg-light-200 text-dark-300'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {index + 1}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        {!showDecision ? (
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl shadow-soft p-8 md:p-12 border border-gray-100"
          >
            <div className="text-center mb-8">
              <motion.span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${salvationSteps[currentStep].gradient} text-white text-sm font-bold mb-6 shadow-lg`}
                whileHover={{ scale: 1.05 }}
              >
                <Gift className="w-4 h-4" />
                Step {currentStep + 1}
              </motion.span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100">
                {salvationSteps[currentStep].title}
              </h2>
            </div>

            <div className="bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 rounded-2xl p-6 md:p-8 mb-8 border border-primary-100">
              <p className="text-xl md:text-2xl text-dark-100 italic mb-3 leading-relaxed">
                "{salvationSteps[currentStep].scripture}"
              </p>
              <p className="text-primary-600 font-bold">
                — {salvationSteps[currentStep].reference}
              </p>
            </div>

            <p className="text-dark-300 text-lg mb-10 text-center leading-relaxed">
              {salvationSteps[currentStep].description}
            </p>

            {/* Navigation */}
            <div className="flex justify-between">
              <motion.button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="px-6 py-3 rounded-xl text-dark-300 hover:bg-light-100 font-semibold transition-colors disabled:opacity-50"
                whileHover={{ scale: currentStep === 0 ? 1 : 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Previous
              </motion.button>

              {currentStep < salvationSteps.length - 1 ? (
                <motion.button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold shadow-lg shadow-primary-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Next Step
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              ) : (
                <motion.button
                  onClick={() => setShowDecision(true)}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold shadow-lg shadow-accent-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Heart className="w-5 h-5" fill="currentColor" />
                  I Want to Receive Christ
                </motion.button>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-soft p-8 md:p-12 border border-gray-100"
          >
            <div className="text-center mb-8">
              <motion.div
                className="w-20 h-20 bg-gradient-to-br from-accent-400 to-accent-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", bounce: 0.5 }}
              >
                <Heart className="w-10 h-10 text-white" fill="currentColor" />
              </motion.div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-4">
                Make Your Decision
              </h2>
              <p className="text-dark-300">
                We'd love to celebrate with you and help you on your journey!
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Your Name"
                  required
                  error={errors.name?.message}
                  {...register('name')}
                />
                <Input
                  label="Email Address"
                  type="email"
                  required
                  error={errors.email?.message}
                  {...register('email')}
                />
              </div>

              <Input
                label="Phone (optional)"
                type="tel"
                {...register('phone')}
              />

              <Select
                label="What decision are you making today?"
                required
                error={errors.decision?.message}
                options={[
                  { value: 'first_time', label: 'Accepting Christ for the first time' },
                  { value: 'rededication', label: 'Rededicating my life to Christ' },
                  { value: 'baptism', label: 'I want to be baptized' },
                  { value: 'membership', label: 'I want to join the church' },
                ]}
                {...register('decision')}
              />

              <Checkbox
                label="I would like someone to contact me to talk more about my decision"
                {...register('wantContact')}
              />

              <div className="flex gap-4">
                <motion.button
                  type="button"
                  onClick={() => setShowDecision(false)}
                  className="px-6 py-3 rounded-xl text-dark-300 hover:bg-light-100 font-semibold transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Back
                </motion.button>
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold shadow-lg shadow-accent-500/25 disabled:opacity-50"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  Submit My Decision
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const NewHere = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/3 right-1/3 w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 left-1/3 w-[500px] h-[500px] bg-secondary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <Heart className="w-4 h-4 text-accent-400" fill="currentColor" />
              Welcome
            </motion.span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
              New <span className="bg-gradient-to-r from-accent-400 to-orange-400 bg-clip-text text-transparent">Here?</span>
            </h1>
            <p className="text-xl text-white/70">
              We're so glad you're exploring! Whether you're just curious about Jesus or ready to
              make a life-changing decision, we're here to walk with you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Salvation Journey */}
      <section className="py-24 bg-gradient-to-b from-light-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="w-4 h-4" />
              Your Salvation Journey
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100 mb-4">
              Discover the Gift of <span className="gradient-text">Eternal Life</span>
            </h2>
            <p className="text-lg text-dark-300 max-w-2xl mx-auto">
              Take a journey through God's plan of salvation. Each step reveals more of His incredible love for you.
            </p>
          </motion.div>

          <SalvationJourney />
        </div>
      </section>

      {/* Already a Believer */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/30"
              whileHover={{ scale: 1.05 }}
            >
              <Users className="w-4 h-4" />
              Join Our Family
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
              Already a Believer?
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              We'd love to meet you! Plan your visit and experience the warmth of our church family.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/plan-your-visit">
                <motion.button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-primary-600 font-bold shadow-xl"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Plan Your Visit
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <Link to="/ministries">
                <motion.button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-white text-white font-bold hover:bg-white/10 transition-colors"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Explore Ministries
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default NewHere
